'use client';

/**
 * Dumb controlled form primitives for the international (Philippines)
 * application wizard. Visual language copied from the local apply page
 * (src/app/recruitment/apply/[id]/page.tsx) with one deliberate change:
 * inputs use text-base (>=16px) so iOS Safari does not zoom on focus -
 * candidates fill this form from their phones.
 */

export function FieldLabel({ label, required }: { label: string; required?: boolean }) {
  return (
    <label className="block text-sm font-medium text-gray-200 mb-1.5">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
  );
}

export function ErrorHint({ show, message }: { show: boolean; message?: string }) {
  if (!show) return null;
  return (
    <p className="mt-1 text-xs text-red-400 flex items-center gap-1">
      <span>⚠</span> {message || 'This field is required.'}
    </p>
  );
}

const INPUT_BASE =
  'w-full rounded-xl px-4 py-2.5 text-base bg-gray-800 border text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:border-transparent transition';

function borderClasses(hasError?: boolean): string {
  return hasError
    ? 'border-red-500 focus:ring-red-500'
    : 'border-gray-700 hover:border-gray-600 focus:ring-emerald-500';
}

export function TextInput({
  value, onChange, placeholder, type = 'text', hasError, readOnly, inputMode,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  hasError?: boolean;
  readOnly?: boolean;
  inputMode?: 'text' | 'email' | 'tel' | 'url' | 'numeric' | 'decimal';
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      readOnly={readOnly}
      inputMode={inputMode}
      className={`${INPUT_BASE} ${readOnly ? 'opacity-70 cursor-default' : ''} ${borderClasses(hasError)}`}
    />
  );
}

export function TextareaInput({
  value, onChange, placeholder, rows = 3, hasError,
}: {
  value: string; onChange: (v: string) => void; placeholder?: string; rows?: number; hasError?: boolean;
}) {
  return (
    <textarea
      rows={rows}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`${INPUT_BASE} resize-none ${borderClasses(hasError)}`}
    />
  );
}

export function NumberInput({
  value, onChange, placeholder, hasError, readOnly, min, max,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hasError?: boolean;
  readOnly?: boolean;
  min?: number;
  max?: number;
}) {
  return (
    <input
      type="number"
      inputMode="decimal"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      readOnly={readOnly}
      min={min}
      max={max}
      className={`${INPUT_BASE} ${readOnly ? 'opacity-70 cursor-default' : ''} ${borderClasses(hasError)}`}
    />
  );
}

export function RadioGroup({
  value, onChange, options, labels, hasError,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
  /** Optional display labels for stored values (e.g. '1_week' → 'Within 1 week'). */
  labels?: Record<string, string>;
  hasError?: boolean;
}) {
  return (
    <div className={`flex flex-wrap gap-2 p-1 rounded-xl transition ${hasError ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-gray-950' : ''}`}>
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition-all ${
            value === opt
              ? 'bg-emerald-600 border-emerald-600 text-white'
              : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-500'
          }`}
        >
          {labels?.[opt] ?? opt}
        </button>
      ))}
    </div>
  );
}

export function SelectInput({
  value, onChange, options, labels, placeholder, hasError,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
  labels?: Record<string, string>;
  placeholder?: string;
  hasError?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`${INPUT_BASE} appearance-none cursor-pointer ${borderClasses(hasError)}`}
    >
      <option value="">{placeholder || 'Select…'}</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>{labels?.[opt] ?? opt}</option>
      ))}
    </select>
  );
}

export function CheckboxGroup({
  values, onChange, options, hasError,
}: {
  values: string[];
  onChange: (v: string[]) => void;
  options: readonly string[];
  hasError?: boolean;
}) {
  const toggle = (opt: string) => {
    onChange(values.includes(opt) ? values.filter((v) => v !== opt) : [...values, opt]);
  };
  return (
    <div className={`grid grid-cols-2 sm:grid-cols-3 gap-2 p-1 rounded-xl transition ${hasError ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-gray-950' : ''}`}>
      {options.map((opt) => {
        const selected = values.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={`px-3 py-2.5 rounded-xl text-sm font-medium border transition-all text-left flex items-center gap-2 ${
              selected
                ? 'bg-emerald-600/20 border-emerald-500 text-emerald-200'
                : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-500'
            }`}
          >
            <span
              className={`w-4 h-4 rounded flex-shrink-0 border flex items-center justify-center ${
                selected ? 'bg-emerald-600 border-emerald-600' : 'border-gray-600'
              }`}
            >
              {selected && (
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </span>
            <span className="min-w-0">{opt}</span>
          </button>
        );
      })}
    </div>
  );
}

export function YesNo({
  value, onChange, hasError,
}: {
  value: boolean | null;
  onChange: (v: boolean) => void;
  hasError?: boolean;
}) {
  return (
    <div className={`flex gap-2 p-1 rounded-xl transition ${hasError ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-gray-950' : ''}`}>
      {([['Yes', true], ['No', false]] as const).map(([label, v]) => (
        <button
          key={label}
          type="button"
          onClick={() => onChange(v)}
          className={`px-6 py-2.5 rounded-xl text-sm font-medium border transition-all ${
            value === v
              ? 'bg-emerald-600 border-emerald-600 text-white'
              : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-500'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export function TickBox({
  checked, onChange, label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}) {
  return (
    <label className="mt-2 flex items-center gap-2 cursor-pointer text-sm text-gray-300">
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`w-4 h-4 rounded flex-shrink-0 border flex items-center justify-center ${
          checked ? 'bg-emerald-600 border-emerald-600' : 'border-gray-600 bg-gray-800'
        }`}
      >
        {checked && (
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </button>
      {label}
    </label>
  );
}

/**
 * Hours + minutes for a duration answer. The form state keeps the total in
 * whole minutes as a string (like number fields); '' = not answered.
 */
export function DurationInput({
  value, onChange, hasError,
}: {
  value: string;
  onChange: (totalMinutes: string) => void;
  hasError?: boolean;
}) {
  const total = value.trim() ? Number(value) : NaN;
  const known = Number.isFinite(total) && total >= 0;
  const hours = known ? String(Math.floor(total / 60)) : '';
  const minutes = known ? String(Math.round(total % 60)) : '';

  const update = (h: string, m: string) => {
    if (!h.trim() && !m.trim()) {
      onChange('');
      return;
    }
    const hn = h.trim() ? Number(h) : 0;
    const mn = m.trim() ? Number(m) : 0;
    if (!Number.isFinite(hn) || !Number.isFinite(mn) || hn < 0 || mn < 0) return;
    onChange(String(Math.round(hn) * 60 + Math.round(mn)));
  };

  const box = `${INPUT_BASE} ${borderClasses(hasError)}`;
  return (
    <div className="flex items-center gap-3">
      <label className="flex items-center gap-2 flex-1">
        <input
          type="number"
          inputMode="numeric"
          min={0}
          max={999}
          value={hours}
          onChange={(e) => update(e.target.value, minutes)}
          placeholder="0"
          className={box}
        />
        <span className="text-sm text-gray-400 flex-shrink-0">hours</span>
      </label>
      <label className="flex items-center gap-2 flex-1">
        <input
          type="number"
          inputMode="numeric"
          min={0}
          max={59}
          value={minutes}
          onChange={(e) => update(hours, e.target.value)}
          placeholder="0"
          className={box}
        />
        <span className="text-sm text-gray-400 flex-shrink-0">minutes</span>
      </label>
    </div>
  );
}
